/**
 * The org's locations as one shared fetch, read by the Users, User details, Locations and
 * Updates surfaces, so a write in one of them shows in all.
 */

import { atom } from 'jotai';

import { snapshotAtoms } from '../snapshotAtoms';
import { api } from '@/api';
import { type StreamInput, StreamInputKind } from '@/types/notifications';
import type { LocationsSnapshot, LocationWithServers } from '@/types/servers';

const PENDING: LocationsSnapshot = {
    locations: [],
    isLoading: true,
    error: null,
};

const locations = snapshotAtoms<LocationsSnapshot>(PENDING, async () => {
    return {
        locations: await api.listLocations(),
        isLoading: false,
        error: null,
    };
});

export const locationsAtom = locations.value;
export const refreshLocationsAtom = locations.reload;

export const locationsByIdAtom = atom((get): Record<string, LocationWithServers> =>
    Object.fromEntries(get(locationsAtom).locations.map((entry) => [entry.id, entry])));

/** What the notifications stream means for the locations. */
export const locationsStreamAtom = atom(null, (_get, set, input: StreamInput): void => {
    if (input.kind === StreamInputKind.Reopened) {
        set(refreshLocationsAtom);
        return;
    }
    if (input.kind !== StreamInputKind.Frame || input.frame.type !== 'change') {
        return;
    }
    // Membership lives on the server row: a server put in a location, taken out of one or
    // deleted arrives as a `servers` frame, and a location's `serverIds` changes with it.
    const { resource } = input.frame;
    if (['locations', 'servers'].includes(resource)) {
        set(refreshLocationsAtom);
    }
});
